/**
 * Monitor tick shared by the dock banner, Sentinel, and the activity tab.
 *
 * Those surfaces used to live in one Live column, so one poll fed all three.
 * Split across tabs they still have to agree, and unmounting the tab that
 * polled would freeze the banner the moment someone left it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Mission,
  MissionDeviceState,
  MissionRouteEvent,
  MissionRouteStatus,
  SentinelAction,
  getMissionRouteEvents,
  runMissionMonitor,
  runSentinelAction,
} from '../../services/missionApi';

const POLL_MS = 8000;

export function useMissionRunWatch(
  mission: Mission | null | undefined,
  opts: {
    onError?: (message: string | null) => void;
    onStatus?: (status: MissionRouteStatus) => void;
    onMissionChange?: () => void;
  } = {},
) {
  const [events, setEvents] = useState<MissionRouteEvent[]>([]);
  const [devices, setDevices] = useState<MissionDeviceState[]>([]);
  const [status, setStatus] = useState<MissionRouteStatus | null>(null);
  const [compareKey, setCompareKey] = useState(0);
  const [checking, setChecking] = useState(false);
  const [routing, setRouting] = useState(false);
  const alive = useRef(true);
  const errorRef = useRef(opts.onError);
  errorRef.current = opts.onError;
  const statusRef = useRef(opts.onStatus);
  statusRef.current = opts.onStatus;
  const changedRef = useRef(opts.onMissionChange);
  changedRef.current = opts.onMissionChange;

  const running = mission?.status === 'in_progress' || mission?.status === 'baseline';
  const hasRoute = !!mission?.route_geometry;
  const hasPhones = (mission?.assignees.length || 0) > 0;
  const monitorable = !!mission && hasRoute && hasPhones;
  const alertsOn = mission?.alerts_enabled ?? true;
  const watching = !!running && alertsOn && monitorable;

  const apply = useCallback((next: MissionRouteStatus) => {
    setEvents(next.events || []);
    setDevices(next.devices || []);
    setStatus(next);
    setCompareKey((k) => k + 1);
    statusRef.current?.(next);
  }, []);

  const load = useCallback(async (missionId: number, evaluate: boolean) => {
    if (evaluate) setChecking(true);
    try {
      const next = evaluate
        ? await runMissionMonitor(missionId)
        : await getMissionRouteEvents(missionId);
      if (!alive.current) return;
      apply(next);
    } catch (e: any) {
      if (alive.current) {
        errorRef.current?.(e?.response?.data?.detail || e?.message || 'Could not check the route');
      }
    } finally {
      if (alive.current) setChecking(false);
    }
  }, [apply]);

  useEffect(() => {
    if (!mission || !monitorable) return undefined;
    alive.current = true;
    load(mission.id, watching);
    if (!watching) return () => { alive.current = false; };
    const timer = setInterval(() => load(mission.id, true), POLL_MS);
    return () => { alive.current = false; clearInterval(timer); };
  }, [load, mission, monitorable, watching]);

  const offRoute = useMemo(
    () => devices.some((d) => d.route_state === 'off_route'),
    [devices],
  );

  const routeAround = useCallback(async (action: SentinelAction) => {
    if (!mission) return;
    setRouting(true);
    try {
      await runSentinelAction(mission.id, action);
      changedRef.current?.();
    } catch (e: any) {
      errorRef.current?.(e?.response?.data?.detail || e?.message || 'Could not ask Sentinel for a route');
    } finally {
      setRouting(false);
    }
  }, [mission]);

  return {
    events,
    devices,
    status,
    checking,
    routing,
    apply,
    offRoute,
    routeAround,
    compareKey,
    hasRoute,
    running,
  };
}
